import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import { NS } from '../../../../i18n/constants/keys';
import { jobOpeningPages } from '../../../../utils/types/JobOpenings';
import { PAGE_LABEL_KEYS } from '../constants';
import { getTabIcon } from '../functions/getTabConfig';
import { ITabStep } from '../interfaces';

export interface JobOpeningTabsProps {
  steps: ITabStep[];
  currentPage: jobOpeningPages;
  setCurrentPage: (page: jobOpeningPages) => void;
  counts?: Partial<Record<string, number>>;
}

export const JobOpeningTabs = ({ steps, currentPage, setCurrentPage, counts = {} }: JobOpeningTabsProps) => {
  const { t } = useTranslation(NS.adminPages);

  const availableSteps = steps.filter((step) => step.status !== 'disabled');
  const configStep = availableSteps.find((s) => s.label === 'Job Opening Details');
  const resultSteps = availableSteps.filter((s) => s.label !== 'Job Opening Details');

  const commonButtonSx = (isActive: boolean) => ({
    height: 38,
    minHeight: 38,
    maxHeight: 38,
    borderRadius: 3,
    px: 1.75,
    py: 0,
    textTransform: 'none' as const,
    borderWidth: isActive ? 1.5 : 1,
    borderColor: isActive ? 'primary.main' : 'grey.300',
    bgcolor: isActive ? 'rgba(0, 111, 238, 0.04)' : 'background.paper',
    fontWeight: isActive ? 700 : 500,
    color: isActive ? 'primary.dark' : 'text.secondary',
    boxShadow: isActive ? '0 0 0 2px rgba(0, 111, 238, 0.12)' : 'none',
    transition: 'all 0.15s ease-in-out',
    '&:hover': {
      borderColor: isActive ? 'primary.main' : 'grey.400',
      bgcolor: isActive ? 'rgba(0, 111, 238, 0.07)' : 'grey.50',
    },
  });

  return (
    <Box sx={{ width: '100%', bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', px: 2, py: 1, overflowX: 'auto' }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 'max-content' }}>
        {/* Job Setup (Configuration) Tab */}
        {configStep && (() => {
          const isActive = currentPage === 'Job Opening Details';
          return (
            <Button
              onClick={() => setCurrentPage('Job Opening Details')}
              variant="outlined"
              size="small"
              sx={commonButtonSx(isActive)}
              startIcon={
                <Box sx={{ color: isActive ? 'primary.main' : 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  {getTabIcon('Job Opening Details', isActive)}
                </Box>
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: isActive ? 700 : 600, lineHeight: 1.15, color: isActive ? 'primary.dark' : 'text.primary' }}>
                  Job Setup
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 500, lineHeight: 1, color: isActive ? 'primary.main' : 'text.secondary' }}>
                  Configuration
                </Typography>
              </Box>
            </Button>
          );
        })()}

        {/* Divider separating Setup and Results */}
        {resultSteps.length > 0 && (
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, borderColor: 'grey.300' }} />
        )}

        {/* Result Tab Pills */}
        {resultSteps.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label="RESULTS"
              size="small"
              sx={{
                height: 24,
                fontSize: '0.62rem',
                fontWeight: 800,
                color: 'primary.main',
                bgcolor: 'primary.50',
                border: '1px solid',
                borderColor: 'primary.200',
                letterSpacing: '0.08em',
                borderRadius: 1.5,
                '& .MuiChip-label': { px: 1 },
              }}
            />
            {resultSteps.map((step) => {
              const isActive = currentPage === step.label;
              const displayLabel = t(PAGE_LABEL_KEYS[step.label] ?? step.label);
              const count = counts[step.label];

              return (
                <Button
                  key={step.label}
                  onClick={() => setCurrentPage(step.label)}
                  variant="outlined"
                  size="small"
                  sx={commonButtonSx(isActive)}
                  startIcon={
                    <Box sx={{ color: isActive ? 'primary.main' : 'text.secondary', display: 'flex', alignItems: 'center' }}>
                      {getTabIcon(step.label, isActive)}
                    </Box>
                  }
                  endIcon={
                    count !== undefined && count > 0 ? (
                      <Chip
                        label={count}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          bgcolor: isActive ? 'primary.50' : 'grey.100',
                          color: isActive ? 'primary.main' : 'text.primary',
                          border: '1px solid',
                          borderColor: isActive ? 'primary.200' : 'grey.300',
                          borderRadius: '9999px',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    ) : undefined
                  }
                >
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: isActive ? 700 : 500, lineHeight: 1.2, color: isActive ? 'primary.dark' : 'text.secondary' }}>
                    {displayLabel}
                  </Typography>
                </Button>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
